import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

// Cloudflare Turnstile bot check (only when the server gives a site key).
//
// PR B2: it runs only when the form is sent (`execution: 'execute'`), not when the
// page opens and again every ~5 minutes. "interaction-only": invisible for most
// people; a checkbox appears in the box only when Cloudflare isn't sure. A token
// works once: every run() starts from a fresh widget. If the widget fails or hangs
// (e.g. error 300030), run() resets it and tries once more by itself.

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  execute(id: string): void;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Form names Cloudflare stores with the token; the server checks the token was made for that form. */
export type BotAction = 'login' | 'forgot' | 'reset' | 'invite';

export interface BotCheckHandle {
  /** A fresh one-time token, or undefined when Turnstile is off. Rejects with a message for the user. */
  run(): Promise<string | undefined>;
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/** A person may need to tick the box: wait this long before giving up. */
const RUN_TIMEOUT_MS = 60_000;
export const BOT_CHECK_FAILED = 'The security check couldn’t run. Check your connection and try again.';
const BOT_CHECK_NO_LOAD = 'The security check couldn’t load. Check your connection and reload the page.';

/** A run() failure (its message is for the user). */
export class BotCheckError extends Error {}

let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')));
    s.onerror = () => {
      loading = null;
      reject(new Error('turnstile script failed to load'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

interface Pending {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
  retried: boolean;
  /** stop the "no answer" timer (a person is ticking the box) */
  hold: () => void;
}

/** A token Cloudflare gave after the form had stopped waiting: used by the next press (valid 300 s). */
const SPARE_MAX_AGE_MS = 240_000;
export const BOT_CHECK_TIMED_OUT = 'The security check timed out. Press the button again.';

export const Turnstile = forwardRef<BotCheckHandle, { siteKey: string | null; action: BotAction; onSolved?: () => void }>(
  function Turnstile({ siteKey, action, onSolved }, ref) {
    const box = useRef<HTMLDivElement>(null);
    const widget = useRef<{ api: TurnstileApi; id: string } | null>(null);
    const ready = useRef<Promise<void> | null>(null);
    const pending = useRef<Pending | null>(null);
    const used = useRef(false);
    const spare = useRef<{ token: string; at: number } | null>(null);
    const interactive = useRef(false);
    const [needsHuman, setNeedsHuman] = useState(false);
    const solved = useRef(onSolved);
    solved.current = onSolved;

    useEffect(() => {
      if (!siteKey) return;
      let gone = false;
      ready.current = loadTurnstile().then((api) => {
        if (gone || !box.current) throw new Error('gone');
        const id = api.render(box.current, {
          sitekey: siteKey,
          action,
          appearance: 'interaction-only',
          execution: 'execute',
          theme: 'auto',
          callback: (token: string) => {
            interactive.current = false;
            setNeedsHuman(false);
            used.current = true;
            const p = pending.current;
            pending.current = null;
            if (p) {
              p.resolve(token);
            } else {
              // solved after the form stopped waiting (e.g. the box was ticked late): keep it for the next press
              spare.current = { token, at: Date.now() };
              solved.current?.();
            }
          },
          // Cloudflare isn't sure: the box appears and waits for a person. That is not a failure:
          // no time limit while it is shown (the person may take a while to notice it).
          'before-interactive-callback': () => {
            interactive.current = true;
            setNeedsHuman(true);
            pending.current?.hold();
            console.info('[CloudPhone] security check needs a tick in the box');
          },
          'after-interactive-callback': () => {
            setNeedsHuman(false);
          },
          'timeout-callback': () => {
            // the box was shown but not ticked in time
            interactive.current = false;
            setNeedsHuman(false);
            const p = pending.current;
            pending.current = null;
            p?.reject(new BotCheckError(BOT_CHECK_TIMED_OUT));
          },
          'error-callback': (code?: string) => {
            const p = pending.current;
            if (interactive.current) {
              // the box is on screen: Cloudflare shows its own "try again" there; keep waiting
              console.info('[CloudPhone] security check error while the box is shown', code ?? '');
              return false;
            }
            if (!p) return true;
            if (!p.retried && widget.current) {
              // one automatic retry (Cloudflare's "widget hung" 300030 and friends)
              p.retried = true;
              console.info('[CloudPhone] security check failed once; trying again', code ?? '');
              widget.current.api.reset(widget.current.id);
              widget.current.api.execute(widget.current.id);
              return true;
            }
            console.info('[CloudPhone] security check failed', code ?? '');
            pending.current = null;
            p.reject(new BotCheckError(BOT_CHECK_FAILED));
            return true; // handled: no console error from Cloudflare's script
          },
          'expired-callback': () => {
            used.current = true; // the next run starts with a reset
            spare.current = null;
          },
        });
        widget.current = { api, id };
      });
      ready.current.catch(() => undefined);
      return () => {
        gone = true;
        pending.current?.reject(new BotCheckError(BOT_CHECK_FAILED));
        pending.current = null;
        if (widget.current) widget.current.api.remove(widget.current.id);
        widget.current = null;
        used.current = false;
        spare.current = null;
        interactive.current = false;
      };
    }, [siteKey, action]);

    useImperativeHandle(
      ref,
      () => ({
        async run() {
          if (!siteKey) return undefined;
          const s = spare.current;
          spare.current = null;
          if (s && Date.now() - s.at < SPARE_MAX_AGE_MS) return s.token;
          try {
            await ready.current;
          } catch {
            throw new BotCheckError(BOT_CHECK_NO_LOAD);
          }
          const w = widget.current;
          if (!w) throw new BotCheckError(BOT_CHECK_NO_LOAD);
          pending.current?.reject(new BotCheckError(BOT_CHECK_FAILED));
          return new Promise<string>((resolve, reject) => {
            let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
              if (pending.current?.resolve === done) pending.current = null;
              reject(new BotCheckError(BOT_CHECK_FAILED));
            }, RUN_TIMEOUT_MS);
            const stop = () => {
              if (timer) clearTimeout(timer);
              timer = null;
            };
            const done = (token: string) => {
              stop();
              resolve(token);
            };
            pending.current = {
              resolve: done,
              reject: (err) => {
                stop();
                reject(err);
              },
              retried: false,
              hold: stop,
            };
            if (interactive.current) {
              stop(); // the box is already waiting for a tick: just wait for it
              return;
            }
            if (used.current) w.api.reset(w.id);
            w.api.execute(w.id);
          });
        },
      }),
      [siteKey],
    );

    if (!siteKey) return null;
    return (
      <div className="turnstile-wrap">
        <div className="turnstile" ref={box} />
        {needsHuman && <p className="turnstile-hint">Tick the box to continue.</p>}
      </div>
    );
  },
);
